import { AnimatePresence, m } from 'framer-motion'
import {
  PointerEvent as ReactPointerEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import KnowledgeBaseArticle from '@/components/knowledge-base/KnowledgeBaseArticle'
import KnowledgeBaseHome from '@/components/knowledge-base/KnowledgeBaseHome'
import KnowledgeBaseSearch from '@/components/knowledge-base/KnowledgeBaseSearch'
import {
  KNOWLEDGE_BASE_HOME,
  parseKnowledgeBaseKey,
} from '@/lib/misc/knowledgeBase'
import { cn } from '@/lib/misc/Utils'
import {
  KNOWLEDGE_BASE_DEFAULT_WIDTH,
  KNOWLEDGE_BASE_QUERY_PARAM,
  KNOWLEDGE_BASE_SHORTCUT_LABEL,
  useKnowledgeBase,
} from '@/lib/provider/KnowledgeBaseProvider'

import ArrowLeftIcon from '@icons/arrow-left'
import ArrowRightIcon from '@icons/arrow-right'
import ArrowsPointingInIcon from '@icons/arrows-pointing-in'
import ArrowsPointingOutIcon from '@icons/arrows-pointing-out'
import CheckIcon from '@icons/check'
import HomeIcon from '@icons/home'
import LinkIcon from '@icons/link'
import MagnifyingGlassIcon from '@icons/magnifying-glass'
import ViewColumnsIcon from '@icons/view-columns'
import WindowIcon from '@icons/window'
import XMarkIcon from '@icons/x-mark'

const FLOATING_INSET = 12
const WIDE_LAYOUT_MIN_WIDTH = 700
const COPIED_RESET_MS = 1500

const panelTransition = {
  type: 'tween' as const,
  ease: [0.32, 0.72, 0, 1] as const,
  duration: 0.42,
}

const isTypingTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable ||
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))

const ControlBar = ({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) => (
  <div
    className={cn(
      'pointer-events-auto flex items-center gap-0.5 rounded-xl border border-hairline/80 bg-surface/80 p-[3px] shadow-raised backdrop-blur-xl backdrop-saturate-150',
      className
    )}
  >
    {children}
  </div>
)

const ControlButton = ({
  label,
  onClick,
  disabled,
  isActive,
  children,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  isActive?: boolean
  children: ReactNode
}) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    aria-pressed={isActive}
    disabled={disabled}
    onClick={onClick}
    className={cn(
      'grid size-7.5 place-items-center rounded-lg transition-colors disabled:cursor-default disabled:opacity-35',
      isActive
        ? 'bg-accent-10 text-accent-100'
        : 'text-neutral-50 enabled:hover:bg-neutral-10 enabled:hover:text-text-base'
    )}
  >
    {children}
  </button>
)

const KnowledgeBasePanel = () => {
  const {
    isEnabled,
    isOpen,
    isDocked,
    canDock,
    isExpanded,
    width,
    current,
    canGoBack,
    canGoForward,
    close,
    navigate,
    replace,
    goBack,
    goForward,
    setDocked,
    toggleExpanded,
    setWidth,
  } = useKnowledgeBase()
  const panelRef = useRef<HTMLElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const [panelWidth, setPanelWidth] = useState(width)
  const [copied, setCopied] = useState(false)

  const isHome = current == null || current === KNOWLEDGE_BASE_HOME
  const isFloating = !isDocked
  const cssWidth = isExpanded && isFloating ? 'min(980px, 82vw)' : `${width}px`

  const openArticle = useCallback(
    (key: string) => {
      setIsSearching(false)
      navigate(key)
    },
    [navigate]
  )

  useEffect(() => {
    if (!isOpen) setIsSearching(false)
  }, [isOpen])

  useEffect(() => {
    const panel = panelRef.current
    if (!isOpen || !panel) return
    const observer = new ResizeObserver(([entry]) =>
      setPanelWidth(entry.contentRect.width)
    )
    observer.observe(panel)
    return () => observer.disconnect()
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSearching && isFloating) {
        close()
        return
      }
      if (
        event.key === '/' &&
        !event.metaKey &&
        !event.ctrlKey &&
        !isTypingTarget(event.target)
      ) {
        event.preventDefault()
        setIsSearching(true)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, isSearching, isFloating, close])

  const onResizeStart = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (isExpanded) toggleExpanded()
    setIsResizing(true)
    const inset = isFloating ? FLOATING_INSET : 0
    const onMove = (move: PointerEvent) =>
      setWidth(window.innerWidth - move.clientX - inset)
    const onUp = () => {
      setIsResizing(false)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  const copyLink = () => {
    if (isHome || !current) return
    const url = new URL(window.location.href)
    url.searchParams.set(KNOWLEDGE_BASE_QUERY_PARAM, current)
    void navigator.clipboard?.writeText(url.toString())
    setCopied(true)
    window.setTimeout(() => setCopied(false), COPIED_RESET_MS)
  }

  if (!isEnabled) return null

  return createPortal(
    <AnimatePresence>
      {isOpen && isFloating && (
        <m.div
          key="knowledge-base-scrim"
          aria-hidden
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
          className="fixed inset-0 z-50 bg-auxiliary-3/15"
        />
      )}
      {isOpen && (
        <m.aside
          key="knowledge-base-panel"
          ref={panelRef}
          role="complementary"
          aria-label="Knowledge base"
          data-cy="knowledge-base-panel"
          initial={{ x: '110%' }}
          animate={{ x: 0 }}
          exit={{ x: '110%' }}
          transition={panelTransition}
          style={{ width: cssWidth }}
          className={cn(
            'fixed flex flex-col overflow-hidden bg-surface',
            isFloating
              ? 'top-3 right-3 bottom-3 z-50 max-w-[calc(100vw-24px)] rounded-[18px] border border-hairline/70 shadow-popover'
              : 'inset-y-0 right-0 z-40 max-w-full border-l border-hairline',
            !isResizing &&
              'transition-[width,top,right,bottom,border-radius] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]'
          )}
        >
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize knowledge base"
            onPointerDown={onResizeStart}
            onDoubleClick={() => setWidth(KNOWLEDGE_BASE_DEFAULT_WIDTH)}
            className={cn(
              'group absolute left-0 z-30 w-2 cursor-col-resize',
              isFloating ? 'inset-y-5' : 'inset-y-0'
            )}
          >
            <span
              className={cn(
                'absolute top-1/2 left-0.5 h-11 w-1 -translate-y-1/2 rounded-full transition-colors',
                isResizing
                  ? 'bg-accent-40'
                  : 'bg-transparent group-hover:bg-accent-40'
              )}
            />
          </div>

          <div className="pointer-events-none absolute inset-x-2.5 top-2.5 z-20 flex items-center gap-2">
            {isSearching ? (
              <KnowledgeBaseSearch
                onClose={() => setIsSearching(false)}
                onSelect={openArticle}
              />
            ) : (
              <>
                <ControlBar>
                  <ControlButton
                    label="Back"
                    onClick={goBack}
                    disabled={!canGoBack}
                  >
                    <ArrowLeftIcon className="size-4" />
                  </ControlButton>
                  <ControlButton
                    label="Forward"
                    onClick={goForward}
                    disabled={!canGoForward}
                  >
                    <ArrowRightIcon className="size-4" />
                  </ControlButton>
                  <ControlButton
                    label="Knowledge base home"
                    onClick={() => navigate(KNOWLEDGE_BASE_HOME)}
                    disabled={isHome}
                  >
                    <HomeIcon className="size-4" />
                  </ControlButton>
                </ControlBar>
                <div className="flex-1" />
                <ControlBar>
                  <ControlButton
                    label="Search (/)"
                    onClick={() => setIsSearching(true)}
                  >
                    <MagnifyingGlassIcon className="size-4" />
                  </ControlButton>
                  <ControlButton
                    label={copied ? 'Link copied' : 'Copy link'}
                    onClick={copyLink}
                    disabled={isHome}
                  >
                    {copied ? (
                      <CheckIcon className="size-4 text-status-good-600" />
                    ) : (
                      <LinkIcon className="size-4" />
                    )}
                  </ControlButton>
                  <span className="mx-[3px] h-4 w-px bg-hairline" />
                  {isFloating && (
                    <ControlButton
                      label={isExpanded ? 'Normal width' : 'Wide reading view'}
                      onClick={toggleExpanded}
                      isActive={isExpanded}
                    >
                      {isExpanded ? (
                        <ArrowsPointingInIcon className="size-4" />
                      ) : (
                        <ArrowsPointingOutIcon className="size-4" />
                      )}
                    </ControlButton>
                  )}
                  {canDock && (
                    <ControlButton
                      label={isDocked ? 'Undock' : 'Dock to page'}
                      onClick={() => setDocked(!isDocked)}
                      isActive={isDocked}
                    >
                      {isDocked ? (
                        <WindowIcon className="size-4" />
                      ) : (
                        <ViewColumnsIcon className="size-4" />
                      )}
                    </ControlButton>
                  )}
                  <ControlButton
                    label={`Close (${isFloating ? 'Esc' : KNOWLEDGE_BASE_SHORTCUT_LABEL})`}
                    onClick={close}
                  >
                    <XMarkIcon className="size-4" />
                  </ControlButton>
                </ControlBar>
              </>
            )}
          </div>

          <div
            ref={bodyRef}
            className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain"
          >
            {isHome ? (
              <KnowledgeBaseHome />
            ) : (
              <KnowledgeBaseArticle
                key={parseKnowledgeBaseKey(current).path}
                target={current}
                scrollContainer={bodyRef}
                isWide={panelWidth >= WIDE_LAYOUT_MIN_WIDTH}
                onNavigate={openArticle}
                onReplace={replace}
              />
            )}
          </div>
        </m.aside>
      )}
    </AnimatePresence>,
    document.body
  )
}

export default KnowledgeBasePanel
